import { useId } from 'react';
import { AXIS_COLOR, AXIS_INK, seriesColor } from './tokens';
import { linearScale, niceTicks, zeroDomain } from './scale';

// =========================================================================
// StackedBar — the demo's `barChart(el, rows, {cats, colors})`, as an SVG
// component. Fork-owned (zone C).
//
// Horizontal, one row per category on the y axis, segments stacked left to
// right in `categories` order. Horizontal because the row labels are mine and
// crew names — "Broken Ridge", "KG Night B" — which do not fit under a
// vertical bar without rotating, and rotated axis labels are a tell.
//
// The `viewBox` is computed from the row count and then fixed, never measured
// from the DOM: the same data renders the same bytes on every machine, which
// is what keeps the parity snapshots stable (plan §F).
//
// Accessibility: the chart is `role="img"` with a generated summary, and the
// same numbers are always available as text — every consuming scene pairs it
// with a table or puts the total on the row. A screen reader is never asked
// to read geometry.
// =========================================================================

/** One stacked row. */
export interface StackedRow {
  /** Row label in the left gutter. */
  readonly label: string;
  /** Segment value by category name. Missing keys count as zero. */
  readonly values: Readonly<Record<string, number>>;
  /** Optional click target — the row becomes a button when set. */
  readonly onSelect?: () => void;
  /** Accessible description of what clicking does. */
  readonly selectLabel?: string;
}

export interface StackedBarProps {
  rows: readonly StackedRow[];
  /** Segment order, left to right. */
  categories: readonly string[];
  /** Category → colour token. Falls back to the ordinal series ramp. */
  colors?: Readonly<Record<string, string>>;
  /** Formats a value for the row total and the tooltips. */
  format: (value: number) => string;
  /**
   * Formats an axis tick. Defaults to {@link StackedBarProps.format}.
   *
   * Separate because the two want different things from zero: a row total of
   * `A$0k` is information, an axis origin labelled `A$0k` is noise, and the
   * money formatter this product uses (`fmtk`) cannot tell which it is being
   * asked for.
   */
  axisFormat?: (value: number) => string;
  /** Axis caption under the chart. */
  axisLabel?: string;
  /** Width of the label gutter, in viewBox units. */
  gutter?: number;
  /** Height of one row, in viewBox units. */
  rowHeight?: number;
  /**
   * viewBox width, in user units.
   *
   * The SVG scales to its container, so every length inside it — including
   * the font sizes — scales by `containerWidth / viewWidth`. A chart authored
   * at 720 and dropped into a 1,120px column renders its labels half again as
   * large as the same chart in a 560px column, and the two stop looking like
   * one system. So the caller states the width it is giving the chart and the
   * text comes out the same size on both. Roughly: 560 for a half column,
   * 1120 for a full one.
   */
  viewWidth?: number;
  /** Accessible name. Required — a chart with no name is unreadable aloud. */
  title: string;
}

const DEFAULT_VIEW_WIDTH = 560;
const PAD_RIGHT = 76;
const PAD_TOP = 10;
const AXIS_HEIGHT = 26;

export function StackedBar({
  rows,
  categories,
  colors,
  format,
  axisFormat,
  axisLabel,
  gutter = 132,
  rowHeight = 30,
  viewWidth = DEFAULT_VIEW_WIDTH,
  title,
}: StackedBarProps) {
  const titleId = useId();
  const plotLeft = gutter;
  const plotRight = viewWidth - PAD_RIGHT;
  const height = PAD_TOP + rows.length * rowHeight + AXIS_HEIGHT;

  const totals = rows.map((r) => categories.reduce((a, c) => a + (r.values[c] ?? 0), 0));
  const domain = zeroDomain(totals);
  const x = linearScale(domain, { min: plotLeft, max: plotRight });
  const ticks = niceTicks(domain, 4);
  const tickLabel = axisFormat ?? format;

  const colorFor = (category: string, index: number): string =>
    colors?.[category] ?? seriesColor(index);

  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm" style={{ color: 'var(--ink-tertiary)' }}>
        No rows to chart.
      </p>
    );
  }

  return (
    <svg
      viewBox={`0 0 ${viewWidth} ${height}`}
      className="w-full"
      style={{ height: 'auto', overflow: 'visible' }}
      role="img"
      aria-labelledby={titleId}
      preserveAspectRatio="xMidYMid meet"
    >
      <title id={titleId}>
        {title}. {rows.length} rows, largest {format(Math.max(...totals))}.
      </title>

      {/* Gridlines behind the bars — hairline only, never a boxed plot area. */}
      {ticks.map((t) => (
        <line
          key={t}
          x1={x(t)}
          x2={x(t)}
          y1={PAD_TOP}
          y2={PAD_TOP + rows.length * rowHeight}
          stroke={AXIS_COLOR}
          strokeWidth={1}
        />
      ))}

      {rows.map((row, i) => {
        const y = PAD_TOP + i * rowHeight;
        const barY = y + rowHeight * 0.2;
        const barH = rowHeight * 0.52;
        let cursor = plotLeft;
        const total = totals[i];

        return (
          <g key={row.label}>
            <text
              x={plotLeft - 10}
              y={y + rowHeight * 0.5 + 4}
              textAnchor="end"
              fontSize={11.5}
              fill="var(--ink-secondary)"
            >
              {row.label}
            </text>

            {categories.map((category, ci) => {
              const value = row.values[category] ?? 0;
              if (value <= 0) return null;
              const w = Math.max(0, x(value) - x(0));
              const segX = cursor;
              cursor += w;
              return (
                <rect
                  key={category}
                  x={segX}
                  y={barY}
                  width={w}
                  height={barH}
                  fill={colorFor(category, ci)}
                  rx={1.5}
                >
                  <title>{`${row.label} · ${category} · ${format(value)}`}</title>
                </rect>
              );
            })}

            <text
              x={plotRight + 8}
              y={y + rowHeight * 0.5 + 4}
              fontSize={11.5}
              fill="var(--ink-primary)"
              className="mono"
            >
              {format(total)}
            </text>
          </g>
        );
      })}

      {/* Axis rule and tick labels. */}
      <line
        x1={plotLeft}
        x2={plotRight}
        y1={PAD_TOP + rows.length * rowHeight}
        y2={PAD_TOP + rows.length * rowHeight}
        stroke={AXIS_COLOR}
        strokeWidth={1}
      />
      {ticks.map((t) => (
        <text
          key={t}
          x={x(t)}
          y={PAD_TOP + rows.length * rowHeight + 14}
          textAnchor="middle"
          fontSize={10}
          fill={AXIS_INK}
          className="mono"
        >
          {tickLabel(t)}
        </text>
      ))}
      {axisLabel ? (
        <text
          x={plotLeft}
          y={height - 2}
          fontSize={10}
          fill={AXIS_INK}
          letterSpacing="0.04em"
        >
          {axisLabel}
        </text>
      ) : null}
    </svg>
  );
}

/**
 * The legend that belongs beside a {@link StackedBar}.
 *
 * Separate component, not a prop, because the legend is HTML text — it must
 * wrap, be selectable and be read in the document flow, none of which an SVG
 * `<text>` does well.
 */
export function StackedBarLegend({
  categories,
  colors,
}: {
  categories: readonly string[];
  colors?: Readonly<Record<string, string>>;
}) {
  return (
    <ul className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      {categories.map((category, i) => (
        <li
          key={category}
          className="flex items-center gap-1.5 text-[11px]"
          style={{ color: 'var(--ink-secondary)' }}
        >
          <span
            aria-hidden="true"
            className="inline-block h-2 w-2 rounded-[2px]"
            style={{ background: colors?.[category] ?? seriesColor(i) }}
          />
          {category}
        </li>
      ))}
    </ul>
  );
}
